import {useState, useEffect, useCallback} from 'react';

interface UseMeetingTimerOptions{
    startedAt: string | undefined;
    tier: 'free' | 'pro' | 'enterprise';
    isHost: boolean;
    onTimeWarning?: (minutesRemaining: number) => void;
    onGracePeriodStart?: () => void;
    onTimeExpired?: () => void;
}

interface UseMeetingTimerReturn{
    timeRemaining: number | null; // in minutes
    formattedTime: string;
    isInGracePeriod: boolean;
    isExpired: boolean;
    progress: number;
}


const TIER_LIMITS ={
    free: 30,
    pro: -1,
    enterprise: -1,
};

export function useMeetingTimer(options: UseMeetingTimerOptions): UseMeetingTimerReturn{
    const {startedAt, tier, isHost, onTimeWarning, onGracePeriodStart,onTimeExpired} = options;
    const [timeRemaining, setTimeRemaining] = useState<number | null>(null);
    const [isInGracePeriod, setIsInGracePeriod] = useState(false);
    const [isExpired, setIsExpired] = useState(false);
    const [hasWarnedAt5min, setHasWarnedAt5min] = useState(false);
     const [hasStartedGrace, setHasStartedGrace] = useState(false);
    
    const maxDuration =TIER_LIMITS[tier]; // in minutes
    const calculateTimeRemaining = useCallback(() => {
        if(!startedAt || maxDuration === -1){
            return null;
        }
        const start = new Date(startedAt).getTime();
        const now = Date.now();
        const elapsedMinutes = (now - start) / (60 * 1000); // convert ms to minutes
        const remaining = maxDuration - elapsedMinutes;
        return Math.max(-5, remaining);
    }, [startedAt, maxDuration]);


    useEffect(()=>{
        if(!startedAt || maxDuration === -1){
            setTimeRemaining(null);
            return;
        }

        //update every second
        const inteval = setInterval(()=>{
            const remaining = calculateTimeRemaining();
            if(remaining === null){
                return
            }
            setTimeRemaining(remaining);

            //check for 5min warning

            if(remaining <= 5 && remaining > 0 && !hasWarnedAt5min){
                onTimeWarning?.(Math.ceil(remaining));
                setHasWarnedAt5min(true);
            }

            //check for grace period start

            if(remaining <= 0 && remaining > -5 && !isInGracePeriod){
                setIsInGracePeriod(true);
                setHasStartedGrace(true);
                onGracePeriodStart?.();
            }

            //check for expiration

            if(remaining <= -5 && !isExpired){
                setIsExpired(true);
                onTimeExpired?.();
            }
    },1000);
    
    return () => clearInterval(inteval);
},[

    startedAt,
    maxDuration,
    calculateTimeRemaining,
    hasWarnedAt5min,
    hasStartedGrace,
    isExpired,
    isHost,
    onTimeWarning,
    onGracePeriodStart,
    onTimeExpired,
]);

const formatTime = (minutes: number | null): string => {
    if(minutes === null){
        return '∞';
    }

    if(minutes < 0){
        const graceRemaining = Math.ceil(5 + minutes);
        return `-0:${graceRemaining < 10 ? '0' : ''}${graceRemaining}`;
    }
     const mins = Math.floor(minutes);
    const secs = Math.floor((minutes - mins) * 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;

}

const calculateProgress = (): number => {
    if (timeRemaining === null || maxDuration === -1) return 0;
    
    const used = maxDuration - timeRemaining;
    return Math.min(100, Math.max(0, (used / maxDuration) * 100));
  };

   return {
    timeRemaining,
    formattedTime: formatTime(timeRemaining),
    isInGracePeriod,
    isExpired,
    progress: calculateProgress(),
  };

}